/*
 * Global cart drawer - Journey B (Gear: tees, stickers) only. Journey A
 * (Shift, product-shift.liquid) calls /cart/clear.js -> /cart/add.js ->
 * /checkout directly and never dispatches the events this file listens
 * for, so it never opens this drawer. See THEME-SPEC §11.
 *
 * Triggers that open the drawer:
 *   - a click on any [data-cart-trigger] (the nav cart icon)
 *   - a `cart:updated` event (dispatched after a successful Gear PDP add,
 *     an add from the drawer's own upsell rows, or a qty/remove change)
 * Never opens on page load or exit intent - nothing in this file calls
 * open() except in response to one of the two triggers above.
 *
 * Panel content (line items, upsell pricing/metafields, empty state) is
 * never hand-built in JS - it's always fetched fresh via the Section
 * Rendering API (`/?sections=cart-drawer`) and swapped into [data-cart-panel]
 * wholesale. This guarantees the drawer can never show content that
 * disagrees with sections/cart-drawer.liquid, since there is no second
 * copy of that markup to fall out of sync.
 */
(() => {
  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;

  const panel = drawer.querySelector('[data-cart-panel]');
  const scrim = document.querySelector('[data-cart-scrim]');
  const root = document.documentElement;

  let lastFocused = null;
  let lastCount = null; // set on first render; badge only pulses on a real change

  // ---- open / close -------------------------------------------------

  const isOpen = () => root.classList.contains('cart-open');

  const trapFocus = (e) => {
    if (e.key !== 'Tab') return;
    const focusable = Array.from(
      panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'),
    ).filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const onKeydown = (e) => {
    if (e.key === 'Escape') {
      close();
    } else {
      trapFocus(e);
    }
  };

  function open() {
    if (isOpen()) return;
    lastFocused = document.activeElement;
    root.classList.add('cart-open');
    drawer.setAttribute('aria-hidden', 'false');
    drawer.inert = false;
    window.__scrollLock?.lock();
    document.addEventListener('keydown', onKeydown);
    document.dispatchEvent(new CustomEvent('cart-drawer:open'));
    const focusTarget = panel.querySelector('[data-cart-close]');
    if (focusTarget) focusTarget.focus();
  }

  function close() {
    if (!isOpen()) return;
    root.classList.remove('cart-open');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.inert = true;
    window.__scrollLock?.unlock();
    document.removeEventListener('keydown', onKeydown);
    document.dispatchEvent(new CustomEvent('cart-drawer:close'));
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  scrim?.addEventListener('click', close);

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-cart-trigger]');
    if (!trigger) return;
    // On /cart itself the nav cart icon stays a plain link to /cart - the
    // drawer never opens there (CART-PAGE-2026-09-11.md rule 9), since the
    // full page already IS the cart. Left as a plain link with no
    // preventDefault, clicking it just reloaded the page it's already on,
    // which read as broken - scroll to the top instead (2026-09-11 fix
    // pass).
    if (document.body.dataset.template === 'cart') {
      e.preventDefault();
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
      return;
    }
    e.preventDefault();
    refresh().then(open);
  });

  // ---- server-rendered content ------------------------------------------
  // panel itself is never replaced (only its innerHTML), so it stays a
  // stable event-delegation target across every refresh.

  function renderPanel(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const newPanel = parsed.querySelector('[data-cart-panel]');
    if (newPanel) panel.innerHTML = newPanel.innerHTML;
  }

  function updateBadges(cart) {
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      const count = cart.item_count;
      badge.hidden = count === 0;
      badge.textContent = count > 99 ? '99+' : String(count);
      badge.classList.toggle('nav-cart__count--wide', count > 9);
    });

    if (lastCount !== null && lastCount !== cart.item_count) {
      document.querySelectorAll('[data-cart-count]').forEach((badge) => {
        badge.classList.remove('is-pulsing');
        // eslint-disable-next-line no-unused-expressions
        badge.offsetWidth; // restart the animation if it fires twice quickly
        badge.classList.add('is-pulsing');
      });
    }
    lastCount = cart.item_count;

    document.querySelectorAll('[data-cart-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-label', ariaLabel(cart.item_count));
    });
  }

  function ariaLabel(count) {
    // __cartIconLabels holds one fully-resolved example string per plural
    // form (count: 1 and count: 2, respectively) - swap out that example
    // number for the real count rather than trying to smuggle a template
    // placeholder through Shopify's `t` filter, which always interpolates.
    const labels = window.__cartIconLabels || { one: 'Cart, 1 item', other: 'Cart, 2 items' };
    const template = count === 1 ? labels.one : labels.other;
    return template.replace(/\d+/, String(count));
  }

  async function refresh() {
    const [cartRes, sectionRes] = await Promise.all([
      fetch('/cart.js', { headers: { Accept: 'application/json' } }),
      fetch('/?sections=cart-drawer', { headers: { Accept: 'application/json' } }),
    ]);
    const cart = await cartRes.json();
    const sections = await sectionRes.json();
    renderPanel(sections['cart-drawer']);
    updateBadges(cart);
    return cart;
  }

  // ---- cart mutations (qty, remove, upsell add) --------------------------

  // Bug #6 (FIX-PLAN-2026-09-11): this used to fire /cart/change.js and
  // immediately dispatch cart:updated without ever reading the response,
  // so an over-requested quantity silently clamped server-side with no
  // sign of it in the drawer. Now mirrors assets/cart-page.js's own
  // changeLine: read back the clamped quantity (from the 200 body, or a
  // fresh /cart.js on a non-200), refresh, then populate that line's
  // error slot via the shared window.showCartStockError (assets/
  // cart-stock-error.js) if the server gave less than was asked for.
  async function sendQtyChange(key, quantity) {
    let res;
    try {
      res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
    } catch (err) {
      console.error(err);
      return;
    }

    if (quantity === 0) {
      await refresh();
      open();
      return;
    }

    let changed;
    if (res.ok) {
      const cart = await res.json();
      changed = cart.items.find((item) => item.key === key);
    } else {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      changed = cart.items.find((item) => item.key === key);
    }

    const needsStockError = Boolean(changed) && changed.quantity < quantity;
    await refresh();
    open();

    if (needsStockError) {
      const freshLine = panel.querySelector(`[data-cart-line][data-line-key="${key}"]`);
      const freshError = freshLine?.querySelector('[data-cart-line-error]');
      if (freshError) window.showCartStockError(freshError, changed);
    }
  }

  // Bug #7 (FIX-PLAN-2026-09-11): a fast double-tap on +/- both read the
  // same not-yet-refreshed DOM quantity as "current", so the second tap's
  // request undid the first instead of building on it. Same queue as
  // assets/cart-page.js: desiredQty tracks the latest target per line key
  // regardless of what's in flight, and a pump loop per key sends one
  // request at a time, looping again if a newer target was queued since
  // the last send - a tap during a request is queued, not dropped.
  const desiredQty = new Map();
  const pumping = new Set();

  function displayedQty(key) {
    const line = panel.querySelector(`[data-cart-line][data-line-key="${key}"]`);
    const valueEl = line?.querySelector('[data-cart-qty-value]');
    return parseInt(valueEl ? valueEl.textContent : '1', 10) || 1;
  }

  async function pumpQty(key) {
    if (pumping.has(key)) return;
    pumping.add(key);
    try {
      let lastSent;
      while (desiredQty.has(key) && desiredQty.get(key) !== lastSent) {
        lastSent = desiredQty.get(key);
        await sendQtyChange(key, lastSent);
      }
      desiredQty.delete(key);
    } finally {
      pumping.delete(key);
    }
  }

  function queueQtyDelta(key, delta) {
    const baseline = desiredQty.has(key) ? desiredQty.get(key) : displayedQty(key);
    desiredQty.set(key, Math.max(0, baseline + delta));
    pumpQty(key);
  }

  function queueQtySet(key, quantity) {
    desiredQty.set(key, quantity);
    pumpQty(key);
  }

  async function addUpsell(link) {
    const row = link.closest('[data-cart-upsell-row]');
    const variantId = row?.getAttribute('data-variant-id');
    if (!variantId) return;

    // A link, not a real <button> - see cart-drawer-upsell-row.liquid's doc
    // comment - so "disabled" while the add is in flight is a class.
    link.classList.add('is-disabled');
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      if (!res.ok) throw new Error('Add failed');
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (err) {
      console.error(err);
      link.classList.remove('is-disabled');
    }
  }

  // Delegated on `panel`, which is never replaced (only its innerHTML is),
  // so this single listener keeps working across every server-rendered swap.
  // Every quantity/remove/upsell control below is a real <a href> so the
  // markup works without JS too (cart-line.liquid, cart-drawer-upsell-row.
  // liquid) - preventDefault stops the plain-link navigation once JS is
  // driving the fetch instead.
  panel.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-close]')) {
      close();
      return;
    }

    const line = e.target.closest('[data-cart-line]');
    if (line) {
      const control = e.target.closest('[data-cart-qty-increase], [data-cart-qty-decrease], [data-cart-remove]');
      if (!control) return;
      e.preventDefault();
      const key = line.getAttribute('data-line-key');

      if (control.hasAttribute('data-cart-qty-increase')) {
        queueQtyDelta(key, 1);
      } else if (control.hasAttribute('data-cart-qty-decrease')) {
        queueQtyDelta(key, -1);
      } else {
        queueQtySet(key, 0);
      }
      return;
    }

    const upsellBtn = e.target.closest('[data-cart-upsell-add]');
    if (upsellBtn) {
      e.preventDefault();
      addUpsell(upsellBtn);
    }
  });

  // Any successful Gear-PDP add, in-drawer upsell add, or qty/remove change
  // fires this - stays open if already open, opens if not (add from PDP).
  document.addEventListener('cart:updated', () => {
    refresh().then(open);
  });

  // Initial paint from the server-rendered `cart` object's own item_count,
  // read once so the very first real add can be detected as a change -
  // never treated as a "change" itself, since nothing has updated yet.
  lastCount = window.__cartInitialCount ?? null;
})();
